/** Shared conversion helpers for Approval Designer vertical flow. */

export const PALETTE_ITEMS = [
  { type: 'singleApprover', label: 'Single Approver', kind: 'lineManager' },
  { type: 'approvalGroup', label: 'Approval Group', kind: 'approvalGroup' },
  { type: 'jobLevel', label: 'Management Hierarchy', kind: 'position' },
  { type: 'aor', label: 'Application Role', kind: 'applicationRole' },
];

const KIND_TO_NODE_TYPE = {
  lineManager: 'singleApprover',
  user: 'singleApprover',
  approvalGroup: 'approvalGroup',
  position: 'jobLevel',
  applicationRole: 'aor',
  autoApprove: 'autoApprove',
};

const NODE_TYPE_DEFAULT_KIND = {
  singleApprover: 'lineManager',
  approvalGroup: 'approvalGroup',
  jobLevel: 'position',
  aor: 'applicationRole',
};

export function kindToNodeType(kind) {
  return KIND_TO_NODE_TYPE[kind] || 'singleApprover';
}

export function nodeTypeToDefaultKind(nodeType) {
  return NODE_TYPE_DEFAULT_KIND[nodeType] || 'lineManager';
}

export function approverDisplayLabel(approver) {
  if (!approver || typeof approver !== 'object') return 'Approver';
  const kind = String(approver.kind || 'approver').replace(/([A-Z])/g, ' $1').trim();
  const ref = approver.ref && approver.ref !== 'n/a' ? ` · ${approver.ref}` : '';
  return `${kind}${ref}`;
}

function isApproverNode(node) {
  return node && !['bpmStart', 'bpmEnd'].includes(node.type);
}

/**
 * Rebuild design.stages from vertical flow graph (top-to-bottom layers).
 * @param {import('@xyflow/react').Node[]} nodes
 * @param {import('@xyflow/react').Edge[]} edges
 * @param {object} baseDesign
 */
export function flowToDesign(nodes, edges, baseDesign = {}) {
  const approverNodes = nodes.filter(isApproverNode);
  if (approverNodes.length === 0) {
    return { ...baseDesign, stages: [] };
  }

  const outgoing = new Map();
  for (const n of nodes) outgoing.set(n.id, []);
  for (const e of edges) {
    if (outgoing.has(e.source)) outgoing.get(e.source).push(e.target);
  }

  const startId = 'bpm-start';
  const layers = [];
  const visited = new Set([startId]);
  let frontier = (outgoing.get(startId) || []).filter((id) => {
    const n = nodes.find((node) => node.id === id);
    return n && isApproverNode(n);
  });

  while (frontier.length > 0) {
    const layer = frontier.map((id) => nodes.find((n) => n.id === id)).filter(Boolean);
    layers.push(layer);
    const next = [];
    for (const node of layer) {
      visited.add(node.id);
      for (const tid of outgoing.get(node.id) || []) {
        if (tid === 'bpm-end') continue;
        const target = nodes.find((n) => n.id === tid);
        if (target && isApproverNode(target) && !visited.has(tid)) {
          next.push(tid);
        }
      }
    }
    frontier = [...new Set(next)];
  }

  if (layers.length === 0) {
    const byOrder = [...approverNodes].sort(
      (a, b) => (Number(a.data?.stageOrder) || 0) - (Number(b.data?.stageOrder) || 0)
    );
    const grouped = new Map();
    for (const n of byOrder) {
      const o = Number(n.data?.stageOrder) || 1;
      if (!grouped.has(o)) grouped.set(o, []);
      grouped.get(o).push(n);
    }
    for (const [, group] of [...grouped.entries()].sort((a, b) => a[0] - b[0])) {
      layers.push(group);
    }
  }

  const stages = layers.map((layer, idx) => {
    const hasAuto = layer.some((n) => n.type === 'autoApprove' || n.data?.kind === 'autoApprove');
    const routing = hasAuto
      ? 'autoApprove'
      : layer.length > 1
        ? 'parallel'
        : layer[0]?.data?.routing === 'parallel'
          ? 'parallel'
          : 'sequential';
    return {
      order: idx + 1,
      routing,
      label: layer[0]?.data?.stageLabel,
      approvers: layer.map((node) => ({
        kind: node.data?.kind || nodeTypeToDefaultKind(node.type),
        ref: node.data?.ref ?? 'n/a',
      })),
    };
  });

  return { ...baseDesign, stages };
}
